import { useSubmit } from "@hyper-fetch/react";
import { ArrowRight, Check, Loader2, MailQuestion, Mails } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { sdk } from "@/api/sdk";
import { GridPattern } from "@/components/magicui/grid-pattern";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

export interface NewsletterSignupProps {
  /** Attribution tag stored with the signup and shown in the admin subscriber list. */
  source?: string;
  /**
   * When set, the panel runs the unsubscribe flow for that token instead of collecting signups:
   * it opts the subscriber out immediately, confirms with a masked address, and offers a way
   * back. This is what the /newsletter/unsubscribe page renders.
   */
  unsubscribeToken?: string;
  /** Override the headline (e.g. a post-article call to action). */
  title?: string;
  /** Override the supporting line. */
  description?: string;
  /** "compact" drops the headline column and decorations for tight hosts like the dock. */
  layout?: "wide" | "compact";
  className?: string;
  /** Fires once a signup succeeds (e.g. so a host dock can retire itself). */
  onSubscribed?: () => void;
}

type PanelState =
  | { step: "subscribe" }
  | { step: "subscribed" }
  | { step: "working" }
  | { step: "invalid" }
  | { step: "unsubscribed"; email: string }
  | { step: "resubscribed"; email: string };

const spring = { type: "spring" as const, stiffness: 380, damping: 26 };

const fade = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.22, ease: [0.22, 1, 0.36, 1] as const },
};

/**
 * THE newsletter component: one wide, glowing panel that handles the whole membership loop.
 * Default mode collects signups (email capsule, success morph); given an unsubscribe token it
 * becomes the one-click opt-out with an undo. Ships unmounted except on the hosted archive.
 */
export function NewsletterSignup({
  source = "app",
  unsubscribeToken,
  title,
  description,
  layout = "wide",
  className,
  onSubscribed,
}: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<PanelState>(unsubscribeToken ? { step: "working" } : { step: "subscribe" });

  const { submit: subscribe, submitting, onSubmitSuccess, onSubmitError } = useSubmit(sdk.newsletter.subscribe.$post);
  const { submit: unsubscribe } = useSubmit(sdk.newsletter.unsubscribe.$post);
  const { submit: resubscribe, submitting: resubscribing } = useSubmit(sdk.newsletter.resubscribe.$post);

  onSubmitSuccess(() => {
    setState({ step: "subscribed" });
    setEmail("");
    onSubscribed?.();
  });
  onSubmitError((error) => {
    toast.error((error as { message?: string }).message ?? m.newsletter_signup_error());
  });

  // Unsubscribe mode acts on load: the token is the credential and the person already decided.
  // Ref guard, not deps: strict mode double-invokes effects and one page view is one opt-out.
  const requested = useRef(false);
  useEffect(() => {
    if (!unsubscribeToken || requested.current) return;
    requested.current = true;

    const run = async () => {
      const { data, error } = await unsubscribe({ payload: { token: unsubscribeToken } });
      if (error || !data) {
        setState({ step: "invalid" });
        return;
      }
      setState({ step: "unsubscribed", email: data.email });
    };
    void run();
  }, [unsubscribeToken, unsubscribe]);

  const handleResubscribe = async () => {
    if (!unsubscribeToken) return;
    const { data, error } = await resubscribe({ payload: { token: unsubscribeToken } });
    if (!error && data) setState({ step: "resubscribed", email: data.email });
  };

  const heading =
    state.step === "invalid"
      ? m.newsletter_unsubscribe_invalid_title()
      : state.step === "unsubscribed"
        ? m.newsletter_unsubscribe_done_title()
        : state.step === "resubscribed"
          ? m.newsletter_resubscribe_done_title()
          : (title ?? m.newsletter_panel_title());

  const supporting =
    state.step === "invalid"
      ? m.newsletter_unsubscribe_invalid_description()
      : state.step === "unsubscribed"
        ? m.newsletter_unsubscribe_done_description({ email: state.email })
        : state.step === "resubscribed"
          ? m.newsletter_resubscribe_done_description({ email: state.email })
          : (description ?? m.newsletter_panel_description());

  const form = (
    <AnimatePresence mode="wait" initial={false}>
      {state.step === "subscribed" ? (
        <motion.div
          key="done"
          {...fade}
          className="border-primary/30 bg-primary/5 flex h-14 items-center gap-3 rounded-2xl border py-1.5 pl-2 pr-5"
          data-tour="newsletter-signup-success"
        >
          <motion.span
            initial={{ scale: 0.4 }}
            animate={{ scale: 1 }}
            transition={{ ...spring, delay: 0.05 }}
            className="bg-primary text-primary-foreground grid size-10 shrink-0 place-items-center rounded-xl"
          >
            <Check className="size-5" strokeWidth={3} />
          </motion.span>
          <p className="min-w-0 flex-1 truncate text-sm font-medium">{m.newsletter_signup_success()}</p>
          <button
            type="button"
            onClick={() => setState({ step: "subscribe" })}
            className="text-muted-foreground hover:text-foreground shrink-0 text-xs font-medium underline-offset-2 transition-colors hover:underline"
          >
            {m.newsletter_signup_again()}
          </button>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          {...fade}
          className={cn(
            "bg-background/90 flex h-14 items-center gap-2 rounded-2xl border p-1.5 pl-5 shadow-sm backdrop-blur transition-all",
            "hover:border-primary/30 focus-within:border-primary/50 focus-within:ring-primary/10 focus-within:ring-4",
          )}
          onSubmit={(event) => {
            event.preventDefault();
            if (!email.trim() || submitting) return;
            subscribe({ payload: { email: email.trim(), source } });
          }}
        >
          <input
            id="newsletter-email"
            type="email"
            required
            autoComplete="email"
            placeholder={m.newsletter_signup_placeholder()}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="placeholder:text-muted-foreground/60 min-w-0 flex-1 bg-transparent text-sm outline-none"
          />
          <button
            type="submit"
            disabled={submitting || !email.trim()}
            className={cn(
              "bg-primary text-primary-foreground flex h-full shrink-0 items-center gap-1.5 rounded-xl px-5 text-sm font-semibold",
              "transition-all hover:brightness-110 active:scale-[0.98] disabled:opacity-35",
            )}
          >
            {submitting ? <Loader2 className="size-4 animate-spin" /> : m.newsletter_signup_submit()}
            {submitting ? null : <ArrowRight className="size-4" />}
          </button>
        </motion.form>
      )}
    </AnimatePresence>
  );

  const unsubscribeBody = (
    <div className="flex flex-col items-center gap-4">
      {state.step === "working" ? (
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      ) : state.step === "unsubscribed" ? (
        <Button variant="outline" className="rounded-xl" onClick={handleResubscribe} disabled={resubscribing}>
          {resubscribing ? m.newsletter_unsubscribe_resubscribing() : m.newsletter_unsubscribe_resubscribe()}
        </Button>
      ) : state.step === "resubscribed" ? (
        <span className="bg-primary/10 text-primary flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium">
          <Check className="size-4" />
          {m.newsletter_signup_success()}
        </span>
      ) : null}
    </div>
  );

  if (layout === "compact") {
    return (
      <div className={className} data-tour="newsletter-signup">
        {form}
      </div>
    );
  }

  return (
    <section
      className={cn("bg-card/40 relative w-full overflow-hidden rounded-3xl border", className)}
      data-tour="newsletter-signup"
    >
      <GridPattern
        width={28}
        height={28}
        className="text-border/50 absolute inset-0 [mask-image:radial-gradient(480px_circle_at_50%_0%,white,transparent)]"
      />
      <div className="bg-primary/10 pointer-events-none absolute -top-24 left-1/2 size-64 -translate-x-1/2 rounded-full blur-3xl" />

      <div className="relative mx-auto flex w-full max-w-xl flex-col items-center px-6 py-12 text-center md:py-16">
        <div className="bg-primary/10 text-primary mb-5 grid size-12 place-items-center rounded-2xl">
          {state.step === "invalid" ? <MailQuestion className="size-5" /> : <Mails className="size-5" />}
        </div>
        <h2 className="text-balance text-2xl font-bold tracking-tight md:text-3xl">{heading}</h2>
        <p className="text-muted-foreground mt-2.5 max-w-md text-balance text-sm leading-6">{supporting}</p>

        <div className="mt-7 w-full">
          {unsubscribeToken ? unsubscribeBody : form}
          {!unsubscribeToken && state.step === "subscribe" ? (
            <p className="text-muted-foreground/80 mt-3 text-xs">{m.newsletter_card_privacy_note()}</p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
